import { useCallback, useState, type FormEvent } from 'react';
import { SPEEDS } from '../lib/constants';
import { spellMs } from '../lib/practice';
import { accuracyPercent, currentMarks, readPerfect } from '../state/practice';
import type { PracticeApi } from '../state/usePractice';
import { PracticeStage } from './PracticeStage';

/**
 * Receptive fingerspelling drill: watch the hand, type what you read. The
 * word is concealed while it is being spelled — the reveal is the feedback.
 */
export function PracticeView({ practice }: { practice: PracticeApi }) {
  const { state } = practice;
  const marks = state.phase === 'feedback' ? currentMarks(state) : null;
  const perfect = state.phase === 'feedback' && readPerfect(state);
  const accuracy = accuracyPercent(state);
  // A drill you cannot see is not a drill: without WebGL there is nothing to
  // read, so say so rather than scoring misses against an empty frame.
  const [rendering, setRendering] = useState(true);
  const onRenderingChange = useCallback((next: boolean) => setRendering(next), []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    practice.submit();
  };

  if (!practice.poolSize) {
    return (
      <section className="practice" aria-label="Fingerspelling practice">
        <p className="kicker">Fingerspelling practice</p>
        <p className="practice-copy">
          The manual alphabet has no complete words to drill yet — letters are only added
          with a sourced form, never guessed. See the signing avatar notes.
        </p>
      </section>
    );
  }

  return (
    <section className="practice" aria-label="Fingerspelling practice">
      <div className="stage-head">
        <p className="kicker stage-label">Fingerspelling · two-handed alphabet</p>
        <p className="stage-chip" data-live={state.phase === 'signing'} aria-live="polite">
          {state.phase === 'ready' && 'READY'}
          {state.phase === 'signing' && 'READ THE HAND'}
          {state.phase === 'answer' && 'YOUR READ'}
          {state.phase === 'feedback' && (perfect ? 'CORRECT' : 'CHECK')}
        </p>
      </div>

      <PracticeStage
        word={state.word}
        phase={state.phase}
        cycleMs={spellMs(state.word || 'A', state.speed)}
        run={state.run}
        marks={marks}
        onRenderingChange={onRenderingChange}
      />

      {!rendering && (
        <p className="practice-copy" role="status">
          This device cannot run the 3D signer, so there is nothing to read here. The
          drill needs the signing stage — the chat still works.
        </p>
      )}

      {/* Place-keeping dots while concealed; marked letters once submitted. */}
      {state.word && !marks && (
        <div className="practice-dots" aria-hidden="true">
          {[...state.word].map((_, index) => (
            <span key={index} className="practice-dot" />
          ))}
        </div>
      )}
      {marks && (
        <div className="practice-reveal" aria-label={`The word was ${state.word}`}>
          {marks.map((mark, index) => (
            <span key={index} className="practice-letter" data-ok={mark.ok}>
              {/* No expected letter means the learner typed past the word's end. */}
              {mark.expected ?? '·'}
              <small>{mark.ok ? ' ' : (mark.got ?? '·')}</small>
            </span>
          ))}
        </div>
      )}

      {state.phase === 'ready' && (
        <div className="practice-block">
          <p className="practice-copy">
            The avatar fingerspells a word — an Australian name, place or everyday word.
            Type what you read. Three clean reads step the speed up.
          </p>
          <div className="practice-actions">
            <button type="button" className="btn-primary" onClick={practice.start} disabled={!rendering}>
              START
            </button>
          </div>
        </div>
      )}

      {state.phase === 'answer' && (
        <form className="practice-block" onSubmit={submit}>
          <label className="visually-hidden" htmlFor="practice-answer">
            Type the word you read
          </label>
          <input
            id="practice-answer"
            className="composer-input practice-input"
            value={state.answer}
            onChange={(event) => practice.setAnswer(event.target.value)}
            placeholder="TYPE WHAT YOU READ"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            /* The input appears at the moment the learner needs it. */
            autoFocus
          />
          <div className="practice-actions">
            <button type="submit" className="btn-primary">
              CHECK
            </button>
            <button
              type="button"
              className="btn-secondary"
              aria-label="SHOW AGAIN — spell the word again before answering"
              onClick={practice.replay}
            >
              SHOW AGAIN
            </button>
          </div>
        </form>
      )}

      {state.phase === 'feedback' && (
        <div className="practice-block">
          <p className="practice-copy" role="status">
            {perfect
              ? `Clean read. ${state.streak} in a row.`
              : 'Compare your read below the letters, then watch it again or move on.'}
          </p>
          <div className="practice-actions">
            <button type="button" className="btn-primary" onClick={practice.next}>
              NEXT WORD
            </button>
            <button
              type="button"
              className="btn-secondary"
              aria-label="WATCH AGAIN — spell the revealed word again"
              onClick={practice.replay}
            >
              WATCH AGAIN
            </button>
          </div>
        </div>
      )}

      <div className="practice-foot">
        <div className="speed-group" role="radiogroup" aria-label="Spelling speed">
          {SPEEDS.map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              className="speed-option"
              aria-checked={option === state.speed}
              onClick={() => practice.setSpeed(option)}
            >
              {option}×
            </button>
          ))}
        </div>
        <dl className="practice-stats">
          <div>
            <dt>STREAK</dt>
            <dd>{state.streak}</dd>
          </div>
          <div>
            <dt>BEST</dt>
            <dd>{state.best}</dd>
          </div>
          <div>
            <dt>ACCURACY</dt>
            <dd>{accuracy === null ? '—' : `${accuracy}%`}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}
